// src/pages/About/OurPrinciples.jsx
import React, { useState } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import { ChevronDown } from 'lucide-react';
import QuerySection from '../../components/common/QuerySection';

const OurPrinciples = () => {
    const [activeDropdown, setActiveDropdown] = useState(null);

    const faqItems = [
        { id: 1, question: "How do investment principles guide portfolio decisions?" },
        { id: 2, question: "What role does asset allocation play in portfolio building?" },
        { id: 3, question: "How do systematic investment plans help in value creation?" },
        { id: 4, question: "What factors influence long-term investment success?" }
    ];

    const toggleDropdown = (id) => {
        setActiveDropdown(activeDropdown === id ? null : id);
    };

    return (
        <>
            <style>{`
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }
                
                .principle-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    cursor: pointer;
                }

                .principle-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }
                
                .hero-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 2rem;
                    padding: 1rem;
                }
                
                .principles-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 2rem;
                    margin-bottom: 4rem;
                }
                
                @media (max-width: 1024px) {
                    .principles-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 1.5rem;
                    }
                }
                
                @media (max-width: 768px) {
                    .principles-grid {
                        grid-template-columns: 1fr;
                        gap: 1.5rem;
                    }
                    
                    .hero-container {
                        padding: 2rem 1rem;
                    }
                }
                
                .hero-title {
                    font-size: clamp(2.5rem, 6vw, 4rem);
                    line-height: 1.1;
                    font-weight: 700;
                }
                
                .section-title {
                    font-size: clamp(2rem, 4vw, 2.5rem);
                    font-weight: 700;
                }
                
                .card-title {
                    font-size: clamp(1.1rem, 2.5vw, 1.4rem);
                    font-weight: 700;
                }
                
                img {
                    max-width: 100%;
                    height: auto;
                    display: block;
                }
                
                .principle-text {
                    font-size: 14px;
                    line-height: 1.5;
                    margin-bottom: 8px;
                }
                
                .principle-text:last-child {
                    margin-bottom: 0;
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
                <Header />

                {/* Hero Section */}
                <section style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    padding: 'clamp(4rem, 8vw, 6rem) 0',
                    position: 'relative',
                    minHeight: '400px'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(59, 130, 246, 0.4) 50%, rgba(0, 0, 0, 0.7) 100%)'
                    }} />
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        position: 'relative',
                        zIndex: 1
                    }} className="hero-container">
                        <div style={{ 
                            textAlign: 'center',
                            maxWidth: '800px',
                            margin: '0 auto'
                        }}>
                            <h1 style={{
                                color: '#ffffff',
                                margin: '0 0 1.5rem 0',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                            }} className="hero-title">
                                32 Core Principles that Guide the WinNest Approach to Investment Planning
                            </h1>
                        </div>
                    </div>
                </section>

                {/* Investment Principles Section */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: 'clamp(3rem, 8vw, 5rem) 0'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }}>
                        <div className="principles-grid">
                            {/* Foundation of Wealth Building */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '0',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }} className="principle-card">
                                <div style={{
                                    padding: '0',
                                    marginBottom: '1rem'
                                }}>
                                    <img 
                                        src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                                        alt="Foundation of Wealth Building" 
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }} className="card-title">
                                        Foundation of Wealth Building
                                    </h3>
                                    <div style={{ color: '#4a5568' }}>
                                        <p className="principle-text">✓ Time and consistency are key factors in building long-term wealth through disciplined investing</p>
                                        <p className="principle-text">✓ Education and understanding help make informed investment decisions aligned with goals</p>
                                        <p className="principle-text">✓ Starting early may potentially benefit from compounding effects over extended periods</p>
                                        <p className="principle-text">✓ Regular systematic investments can help manage market volatility through averaging</p>
                                        <p className="principle-text">✓ Financial planning should balance wealth accumulation with life goals and happiness</p>
                                    </div>
                                </div>
                            </div>

                            {/* Risk Assessment and Management */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '0',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }} className="principle-card">
                                <div style={{
                                    padding: '0',
                                    marginBottom: '1rem'
                                }}>
                                    <img 
                                        src="https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                                        alt="Risk Assessment and Management" 
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }} className="card-title">
                                        Risk Assessment and Management
                                    </h3>
                                    <div style={{ color: '#4a5568' }}>
                                        <p className="principle-text">✓ Risk management forms the foundation of sustainable investment planning strategies</p>
                                        <p className="principle-text">✓ Understanding various risks helps in making appropriate investment choices</p>
                                        <p className="principle-text">✓ Liquidity planning ensures access to funds when needed for goals or emergencies</p>
                                        <p className="principle-text">✓ Market volatility may present both challenges and potential opportunities</p>
                                        <p className="principle-text">✓ Regular portfolio review helps maintain alignment with risk tolerance and objectives</p>
                                    </div>
                                </div>
                            </div>

                            {/* Portfolio Design and Asset Distribution */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '0',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }} className="principle-card">
                                <div style={{
                                    padding: '0',
                                    marginBottom: '1rem'
                                }}>
                                    <img 
                                        src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                                        alt="Portfolio Design and Asset Distribution" 
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }} className="card-title">
                                        Portfolio Design and Asset Distribution
                                    </h3>
                                    <div style={{ color: '#4a5568' }}>
                                        <p className="principle-text">✓ Strategic asset allocation may help optimize portfolio performance based on goals</p>
                                        <p className="principle-text">✓ Diversification across asset classes can help manage concentration risk</p>
                                        <p className="principle-text">✓ Geographic and sectoral spread may reduce dependency on single markets</p>
                                        <p className="principle-text">✓ Quality fund selection should consider track record and management expertise</p>
                                        <p className="principle-text">✓ Cost awareness helps in maximizing net returns from investment portfolios</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="principles-grid">
                            {/* Systematic Investment and Cash Flow */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '0',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }} className="principle-card">
                                <div style={{
                                    padding: '0',
                                    marginBottom: '1rem'
                                }}>
                                    <img 
                                        src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                                        alt="Systematic Investment and Cash Flow" 
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }} className="card-title">
                                        Systematic Investment and Cash Flow
                                    </h3>
                                    <div style={{ color: '#4a5568' }}>
                                        <p className="principle-text">✓ SIPs help build investment discipline through regular, automated contributions</p>
                                        <p className="principle-text">✓ SWPs may provide systematic cash flow during retirement or goal fulfillment phases</p>
                                        <p className="principle-text">✓ Emergency funds ensure financial stability during unexpected situations</p>
                                        <p className="principle-text">✓ Cash flow planning aligns investment maturity with financial requirements</p>
                                        <p className="principle-text">✓ Tax efficiency considerations may help optimize post-tax returns</p>
                                        <p className="principle-text">✓ Flexible structures allow adjustments based on changing circumstances</p>
                                        <p className="principle-text">✓ Regular monitoring ensures investment strategies remain goal-oriented</p>
                                        <p className="principle-text">✓ Dynamic approaches adapt to market conditions and life stage changes</p>
                                        <p className="principle-text">✓ Integrated planning combines multiple investment tools for optimal outcomes</p>
                                    </div>
                                </div>
                            </div>

                            {/* Behavioral Discipline and Decision Making */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '0',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }} className="principle-card">
                                <div style={{
                                    padding: '0',
                                    marginBottom: '1rem'
                                }}>
                                    <img 
                                        src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                                        alt="Behavioral Discipline and Decision Making" 
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }} className="card-title">
                                        Behavioral Discipline and Decision Making
                                    </h3>
                                    <div style={{ color: '#4a5568' }}>
                                        <p className="principle-text">✓ Long-term perspective helps avoid emotional decisions during market fluctuations</p>
                                        <p className="principle-text">✓ Systematic approaches reduce the impact of behavioral biases on investment outcomes</p>
                                        <p className="principle-text">✓ Regular review and rebalancing maintain portfolio alignment with investment objectives</p>
                                    </div>
                                </div>
                            </div>

                            {/* Comprehensive Financial Wellness */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '0',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }} className="principle-card">
                                <div style={{
                                    padding: '0',
                                    marginBottom: '1rem'
                                }}>
                                    <img 
                                        src="https://images.unsplash.com/photo-1434626881859-194d67b2b86f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                                        alt="Comprehensive Financial Wellness" 
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{
                                        color: '#1a1a1a',
                                        marginBottom: '1rem'
                                    }} className="card-title">
                                        Comprehensive Financial Wellness
                                    </h3>
                                    <div style={{ color: '#4a5568' }}>
                                        <p className="principle-text">✓ Holistic planning considers all aspects of financial health and life goals</p>
                                        <p className="principle-text">✓ Financial freedom involves balancing wealth creation with current life satisfaction</p>
                                        <p className="principle-text">✓ Goal-based investing aligns financial strategies with specific life objectives</p>
                                        <p className="principle-text">✓ Wealth management should support overall well-being and quality of life</p>
                                        <p className="principle-text">✓ Balanced approach integrates accumulation, preservation, and distribution phases</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <Footer />
            </div>
        </>
    );
};

export default OurPrinciples;